import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MvCard from "@/components/feature/MvCard";
import { genres, mvList } from "@/mocks/mv";
import { useSeo } from "@/hooks/useSeo";
import { formatViews } from "@/utils/format";

export default function DetailPage() {
  const { id } = useParams();
  const mv = mvList.find((item) => item.id === id);
  const [playing, setPlaying] = useState(false);
  const [toast, setToast] = useState("");
  const [liked, setLiked] = useState(false);

  useSeo({
    title: mv ? `红桃MV视频 - MV详情 - ${mv.title}` : "红桃MV视频 - MV详情",
    description: mv
      ? `《${mv.title}》是歌手${mv.artist}的作品，发布时间${mv.releaseDate}，属于${mv.region}${mv.tags[0] || "音乐"}类目。在红桃MV视频在线观看高清 MV。`
      : "红桃MV视频 MV 详情页。",
    keywords: mv ? `${mv.title},${mv.artist},${mv.tags.join(",")},红桃MV视频` : "红桃MV视频,MV详情",
  });

  const genreInfo = genres.find((g) => g.code === mv?.genreCode);

  const related = useMemo(() => {
    if (!mv) return [];
    const sameGenreOrArtist = mvList.filter(
      (item) => item.id !== mv.id && (item.genreCode === mv.genreCode || item.artist === mv.artist)
    );
    if (sameGenreOrArtist.length >= 4) return sameGenreOrArtist.slice(0, 4);
    const extra = mvList
      .filter((item) => item.id !== mv.id && !sameGenreOrArtist.includes(item))
      .sort((a, b) => b.trending - a.trending)
      .slice(0, 4 - sameGenreOrArtist.length);
    return [...sameGenreOrArtist, ...extra];
  }, [mv]);

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      showToast("链接已复制到剪贴板（前端演示）");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  if (!mv) {
    return (
      <div className="flex w-full flex-col items-center justify-center px-4 py-24 text-center">
        <i className="ri-video-off-line text-5xl text-secondary-500" aria-hidden="true"></i>
        <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-50">MV 不存在或已下架</h1>
        <p className="mt-2 text-sm text-foreground-400">该 MV 可能已被移除，去看看其他精彩内容吧。</p>
        <div className="mt-6 flex gap-3">
          <Link to="/" className="rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">返回首页</Link>
          <Link to="/charts" className="rounded-md border border-background-700 px-5 py-2.5 text-sm text-foreground-300 hover:border-primary-500">排行榜</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full">
      {toast && (
        <div className="fixed left-1/2 top-20 z-[60] -translate-x-1/2 rounded-md bg-background-800 px-4 py-2.5 text-sm text-foreground-100 border border-background-700">
          {toast}
        </div>
      )}

      <div className="bg-background-900/50 px-4 py-6 md:px-8">
        <nav className="mb-4 flex flex-wrap items-center gap-1 text-xs text-foreground-500" aria-label="面包屑">
          <Link to="/" className="hover:text-primary-400">首页</Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          {genreInfo && (
            <>
              <Link to={`/category/${genreInfo.code}`} className="hover:text-primary-400">{genreInfo.name}</Link>
              <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            </>
          )}
          <span className="text-foreground-200">{mv.title}</span>
        </nav>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
          {/* Player */}
          <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-background-800 bg-background-950">
            <img src={mv.cover} alt={`${mv.title} 封面`} title={`${mv.title} - ${mv.artist}`} className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-background-950/45"></div>

            {playing ? (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
                <div className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-3xl text-background-950 animate-soft-pulse">
                  <i className="ri-pause-fill" aria-hidden="true"></i>
                </div>
                <p className="rounded-md bg-background-950/70 px-3 py-1.5 text-xs text-foreground-300">
                  演示播放器 · 正式视频播放接口待接入
                </p>
                <div className="w-4/5">
                  <div className="h-1 w-full overflow-hidden rounded-full bg-foreground-500/30">
                    <div className="h-full w-1/3 rounded-full bg-accent-500"></div>
                  </div>
                </div>
                <button type="button" onClick={() => setPlaying(false)} className="cursor-pointer rounded-md border border-foreground-400/40 px-4 py-2 text-xs text-foreground-200 hover:border-foreground-100">
                  停止演示
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setPlaying(true)}
                aria-label="播放 MV"
                className="absolute inset-0 flex flex-col items-center justify-center gap-4"
              >
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-accent-500 text-3xl text-background-950 transition-transform hover:scale-105 md:h-20 md:w-20">
                  <i className="ri-play-fill" aria-hidden="true"></i>
                </span>
                <span className="rounded-md bg-background-950/70 px-3 py-1.5 text-xs text-foreground-300">
                  点击播放（前端演示）
                </span>
              </button>
            )}
          </div>

          {/* Side info */}
          <div className="rounded-lg border border-background-800 bg-background-900 p-5">
            <h1 className="font-heading text-2xl font-bold text-foreground-50">{mv.title}</h1>
            <p className="mt-2 flex items-center gap-2 text-sm text-foreground-300">
              <i className="ri-user-3-line text-primary-400" aria-hidden="true"></i>
              {mv.artist}
            </p>
            <div className="mt-4 space-y-2 text-sm text-foreground-400">
              <p className="flex items-center gap-2"><i className="ri-calendar-line text-primary-400" aria-hidden="true"></i>发布时间：{mv.releaseDate}</p>
              <p className="flex items-center gap-2"><i className="ri-time-line text-primary-400" aria-hidden="true"></i>时长：{mv.duration}</p>
              <p className="flex items-center gap-2"><i className="ri-earth-line text-primary-400" aria-hidden="true"></i>地区：{mv.region}</p>
              <p className="flex items-center gap-2"><i className="ri-play-circle-line text-primary-400" aria-hidden="true"></i>播放量：{formatViews(mv.views)}</p>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {genreInfo && (
                <Link to={`/category/${genreInfo.code}`} className="rounded-full bg-primary-500/15 px-3 py-1 text-xs text-primary-300 hover:bg-primary-500/25">
                  {genreInfo.name}
                </Link>
              )}
              {mv.tags.map((t) => (
                <span key={t} className="rounded-full bg-secondary-500/15 px-3 py-1 text-xs text-secondary-300">{t}</span>
              ))}
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <button type="button" onClick={() => setPlaying(true)} className="flex flex-1 cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2.5 text-sm font-medium text-background-950 hover:bg-accent-400">
                <i className="ri-play-fill text-lg" aria-hidden="true"></i>
                播放
              </button>
              <button type="button" onClick={() => setLiked((v) => !v)} className={`flex cursor-pointer items-center justify-center gap-1 whitespace-nowrap rounded-md border px-4 py-2.5 text-sm ${liked ? "border-primary-500 text-primary-400" : "border-background-700 text-foreground-300 hover:border-foreground-100"}`}>
                <i className={liked ? "ri-heart-3-fill" : "ri-heart-3-line"} aria-hidden="true"></i>
                {liked ? "已收藏" : "收藏"}
              </button>
            </div>

            <div className="mt-4 border-t border-background-800 pt-4">
              <p className="mb-2 text-xs text-foreground-500">分享这支 MV</p>
              <div className="flex flex-wrap gap-2">
                <button type="button" onClick={copyLink} className="flex cursor-pointer items-center gap-1 rounded-md border border-background-700 px-3 py-1.5 text-xs text-foreground-300 hover:border-primary-500 hover:text-primary-400">
                  <i className="ri-link" aria-hidden="true"></i>复制链接
                </button>
                <button type="button" onClick={() => showToast("已分享到微博（前端演示）")} className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-700 text-foreground-300 hover:border-primary-500 hover:text-primary-400">
                  <i className="ri-weibo-line" aria-hidden="true"></i>
                </button>
                <button type="button" onClick={() => showToast("已分享到微信（前端演示）")} className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-700 text-foreground-300 hover:border-primary-500 hover:text-primary-400">
                  <i className="ri-wechat-line" aria-hidden="true"></i>
                </button>
                <button type="button" onClick={() => showToast("已分享到 X（前端演示）")} className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border border-background-700 text-foreground-300 hover:border-primary-500 hover:text-primary-400">
                  <i className="ri-twitter-x-line" aria-hidden="true"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full px-4 py-8 md:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_320px]">
          <div>
            <h2 className="mb-3 font-heading text-xl font-bold text-foreground-50">简介</h2>
            <p className="text-sm leading-7 text-foreground-300">{mv.description}</p>

            <h2 className="mb-4 mt-10 font-heading text-xl font-bold text-foreground-50">相关推荐</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
              {related.map((item) => (
                <MvCard key={item.id} mv={item} />
              ))}
            </div>
          </div>

          <aside>
            <h2 className="mb-4 font-heading text-lg font-bold text-foreground-50">人气 MV</h2>
            <div className="space-y-3">
              {[...mvList]
                .sort((a, b) => b.trending - a.trending)
                .slice(0, 6)
                .map((item, i) => (
                  <Link key={item.id} to={`/detail/${item.id}`} className="group flex items-center gap-3 rounded-lg border border-background-800 bg-background-900 p-2 transition-colors hover:border-primary-500/60">
                    <span className={`w-5 text-center font-heading text-base font-bold ${i < 3 ? "text-accent-500" : "text-foreground-500"}`}>{i + 1}</span>
                    <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-md">
                      <img src={item.cover} alt={`${item.title} 缩略图`} title={`${item.title} - ${item.artist}`} className="h-full w-full object-cover" />
                    </div>
                    <div className="min-w-0">
                      <p className="truncate text-sm text-foreground-100 group-hover:text-primary-400">{item.title}</p>
                      <p className="truncate text-xs text-foreground-500">{item.artist}</p>
                    </div>
                  </Link>
                ))}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}